import { EditableText } from '@/components/editor/EditableText';
import { renderMarkdownLite } from '@/lib/markdown';
import type { Fiche, FicheSection } from '@pharmafiche/schema';
import { Fragment } from 'react';
import type { FicheEditHandlers } from './FicheDessinee';

interface FicheMinimalisteProps {
  fiche: Fiche;
  editHandlers?: FicheEditHandlers;
}

/**
 * Phase 6 — exam-cram template.
 *
 * Pure white. EB Garamond throughout. No colors, no shadows, no rotation.
 * 2-column ultra-dense layout. Italics carry emphasis (never bold-color).
 * Print-first: tight line-height, no widows/orphans, `†` for warnings.
 */
export function FicheMinimaliste({ fiche, editHandlers }: FicheMinimalisteProps) {
  return (
    <div
      className="min-h-screen bg-white text-ink font-garamond"
      style={{ padding: '40px 12mm', WebkitFontSmoothing: 'antialiased' }}
    >
      <div className="max-w-[820px] mx-auto">
        <header className="text-center mb-6">
          <h1
            className="font-garamond font-bold text-[28px] leading-tight"
            style={{ letterSpacing: '0.01em' }}
          >
            <EditableText
              value={fiche.meta.title}
              plain
              ariaLabel="Titre"
              onCommit={(t) => editHandlers?.updateMetaTitle?.(t)}
            />
          </h1>
          <hr className="mt-2 border-0 border-t border-ink/40 mx-auto w-24" />
          {fiche.meta.subtitle ? (
            <p className="font-garamond italic text-[14px] mt-1 text-ink-soft">
              {fiche.meta.subtitle}
            </p>
          ) : null}
        </header>

        <div className="columns-1 md:columns-2 gap-8 text-[13px] leading-[1.42]">
          {fiche.sections.map((sec, i) => (
            <SectionRenderer key={sec.id} section={sec} num={i + 1} editHandlers={editHandlers} />
          ))}
        </div>

        <footer className="mt-10 pt-3 border-t border-ink/30 text-center font-garamond italic text-[11px] text-ink-soft">
          {fiche.meta.title} — PharmaFiche
        </footer>
      </div>
    </div>
  );
}

function SectionRenderer({
  section,
  num,
  editHandlers,
}: {
  section: FicheSection;
  num: number;
  editHandlers?: FicheEditHandlers;
}) {
  return (
    <section
      className="mb-5 break-inside-avoid"
      data-section-id={section.id}
      style={{ pageBreakInside: 'avoid' }}
    >
      {renderHead(section, num, editHandlers)}
      {renderBody(section, editHandlers)}
    </section>
  );
}

function renderHead(section: FicheSection, num: number, editHandlers?: FicheEditHandlers) {
  if (section.type === 'mnemonic') return null;
  if (section.type === 'warning') return null;
  if (section.type === 'diagram') return null;
  if (section.type === 'comparison_table') {
    return (
      <h2 className="font-garamond font-bold text-[14px] uppercase tracking-wider mb-1 border-b border-ink/30 pb-[2px]">
        {num}. {section.title}
      </h2>
    );
  }
  if (section.type === 'drug_card') {
    return (
      <h2 className="font-garamond font-bold text-[14px] uppercase tracking-wider mb-1 border-b border-ink/30 pb-[2px]">
        {num}.{' '}
        <EditableText
          value={section.drug.name}
          plain
          ariaLabel="Nom de la molécule"
          onCommit={(t) => editHandlers?.updateDrugName?.(section.id, t)}
        />
      </h2>
    );
  }
  if (section.type === 'clinical_case') {
    return (
      <h2 className="font-garamond font-bold text-[14px] uppercase tracking-wider mb-1 border-b border-ink/30 pb-[2px]">
        {num}. Cas clinique
      </h2>
    );
  }
  if (section.type === 'free_text') {
    return section.title ? (
      <h2 className="font-garamond font-bold text-[14px] uppercase tracking-wider mb-1 border-b border-ink/30 pb-[2px]">
        {num}. {section.title}
      </h2>
    ) : null;
  }
  // intro / classification
  return (
    <h2 className="font-garamond font-bold text-[14px] uppercase tracking-wider mb-1 border-b border-ink/30 pb-[2px]">
      {num}.{' '}
      <EditableText
        value={section.title}
        plain
        ariaLabel="Titre de section"
        onCommit={(t) => editHandlers?.updateSectionTitle?.(section.id, t)}
      />
    </h2>
  );
}

function renderBody(section: FicheSection, editHandlers?: FicheEditHandlers) {
  if (section.type === 'intro' || section.type === 'classification') {
    return (
      <>
        <BulletList
          bullets={section.bullets}
          onCommit={(id, t) => editHandlers?.updateBulletText?.(section.id, id, t)}
        />
        {section.type === 'intro' && section.warning ? (
          <p className="italic mt-1 text-[12.5px]">
            <span aria-hidden>† </span>
            <EditableText
              value={section.warning}
              ariaLabel="Avertissement"
              onCommit={(t) => editHandlers?.updateSectionWarning?.(section.id, t)}
            />
          </p>
        ) : null}
        {section.type === 'classification' && section.note ? (
          <p className="italic mt-1 text-[12.5px]">
            <span aria-hidden>† </span>
            <EditableText
              value={section.note}
              ariaLabel="Note"
              onCommit={(t) => editHandlers?.updateSectionWarning?.(section.id, t)}
            />
          </p>
        ) : null}
      </>
    );
  }
  if (section.type === 'drug_card') {
    return (
      <>
        <BulletList
          bullets={section.drug.intro_bullets}
          onCommit={(id, t) => editHandlers?.updateBulletText?.(section.id, id, t)}
        />
        {section.drug.sub_sections.map((ss) => (
          <Fragment key={ss.id}>
            <h3 className="font-garamond italic text-[12.5px] mt-2 mb-[2px]">
              <EditableText
                value={ss.label}
                plain
                ariaLabel="Étiquette sous-section"
                onCommit={(t) => editHandlers?.updateDrugSubsectionLabel?.(section.id, ss.id, t)}
              />
            </h3>
            <BulletList
              bullets={ss.bullets}
              onCommit={(id, t) =>
                editHandlers?.updateDrugSubsectionBulletText?.(section.id, ss.id, id, t)
              }
            />
          </Fragment>
        ))}
      </>
    );
  }
  if (section.type === 'clinical_case') {
    return (
      <>
        <p className="italic mt-1 text-[12.5px]">
          <EditableText
            value={section.patient}
            ariaLabel="Patient"
            onCommit={(t) => editHandlers?.updateClinicalPatient?.(section.id, t)}
          />
        </p>
        <ul className="mt-1 list-disc pl-5">
          {section.prescription.map((p, i) => (
            <li key={`${section.id}-rx-${i}`} className="text-[12.5px]">
              {renderMarkdownLite(p)}
            </li>
          ))}
        </ul>
        <p className="mt-1 text-[12.5px]">
          <strong>Question : </strong>
          <EditableText
            value={section.question}
            ariaLabel="Question"
            onCommit={(t) => editHandlers?.updateClinicalQuestion?.(section.id, t)}
          />
        </p>
        <h3 className="font-garamond italic text-[12.5px] mt-2 mb-[2px]">Conduite à tenir</h3>
        <BulletList
          bullets={section.conduite}
          onCommit={(id, t) => editHandlers?.updateBulletText?.(section.id, id, t)}
        />
      </>
    );
  }
  if (section.type === 'mnemonic') {
    return (
      <p className="italic mt-2 text-[13px]" data-section-id={section.id}>
        <span aria-hidden>♦ </span>
        <EditableText
          value={section.text}
          ariaLabel="Mnémo"
          onCommit={(t) => editHandlers?.updateMnemonicText?.(section.id, t)}
        />
      </p>
    );
  }
  if (section.type === 'warning') {
    return (
      <p className="italic mt-2 text-[12.5px]" data-section-id={section.id}>
        <span aria-hidden>† </span>
        <EditableText
          value={section.text}
          ariaLabel="Avertissement"
          onCommit={(t) => editHandlers?.updateWarningText?.(section.id, t)}
        />
      </p>
    );
  }
  if (section.type === 'diagram') {
    return (
      <p className="italic mt-1 text-[12px] text-ink-soft">
        [diagramme : voir version Flowchart de cette fiche]
      </p>
    );
  }
  if (section.type === 'comparison_table') {
    return (
      <table
        className="w-full text-[12px] mt-1 border-collapse"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        <thead>
          <tr className="border-b border-ink">
            <th className="text-left font-garamond italic pr-2">&nbsp;</th>
            {section.columns.map((c) => (
              <th key={c} className="text-left font-garamond italic pr-2">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {section.rows.map((r) => (
            <tr key={r.label} className="border-b border-ink/20">
              <td className="font-garamond font-bold py-1 pr-2">{r.label}</td>
              {r.cells.map((c, j) => (
                <td key={`${r.label}-${j}`} className="py-1 pr-2">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (section.type === 'free_text') {
    return <p className="text-[12.5px] whitespace-pre-wrap">{section.body}</p>;
  }
  return null;
}

function BulletList({
  bullets,
  onCommit,
}: {
  bullets: { id: string; text: string }[];
  onCommit: (id: string, text: string) => void;
}) {
  return (
    <ul className="m-0 mt-1 pl-5 list-disc list-outside">
      {bullets.map((b) => (
        <li key={b.id} className="text-[12.5px] leading-[1.42]" data-bullet-id={b.id}>
          <EditableText value={b.text} ariaLabel="Bullet" onCommit={(t) => onCommit(b.id, t)} />
        </li>
      ))}
    </ul>
  );
}
